import {
  AdditiveBlending,
  Color,
  DoubleSide,
  DynamicDrawUsage,
  InstancedBufferAttribute,
  InstancedMesh,
  Matrix4,
  MeshBasicNodeMaterial,
  PlaneGeometry,
  Quaternion,
  Vector3,
} from 'three/webgpu';
import {
  abs,
  atan,
  float,
  fract,
  hash,
  instancedBufferAttribute,
  instanceIndex,
  length,
  max,
  mix,
  mx_noise_float,
  oneMinus,
  smoothstep,
  time,
  uv,
  vec2,
  vec3,
  vec4,
} from 'three/tsl';
import { Config } from '@src/core/Config';

const tmpMat = new Matrix4();
const tmpPos = new Vector3();
const tmpQuat = new Quaternion();
const tmpScale = new Vector3();
const tmpColor = new Color();
const Z_AXIS = new Vector3(0, 0, 1);
const TAU = 6.28318530718;

/**
 * Additive aura under each portal: floor glow, rune rim, vortex arms, and
 * wound cracks. One quad per spawner; damage and death ride a vec4 attribute.
 */
export class PortalAura {
  readonly mesh: InstancedMesh;
  private readonly capacity = Config.pools.spawners;
  private readonly state: InstancedBufferAttribute;
  private readonly alpha: InstancedBufferAttribute;
  private count = 0;

  constructor() {
    const geometry = new PlaneGeometry(1, 1);
    this.state = new InstancedBufferAttribute(new Float32Array(this.capacity * 4), 4);
    this.state.setUsage(DynamicDrawUsage);
    this.alpha = new InstancedBufferAttribute(new Float32Array(this.capacity).fill(1), 1);
    this.alpha.setUsage(DynamicDrawUsage);

    const material = new MeshBasicNodeMaterial();
    material.transparent = true;
    material.depthWrite = false;
    material.depthTest = false;
    material.side = DoubleSide;
    material.blending = AdditiveBlending;

    const p = uv().sub(0.5).mul(2);
    const d = length(p);
    const ang = atan(p.y, p.x);
    const st = instancedBufferAttribute<'vec4'>(this.state, 'vec4');
    const alphaNode = instancedBufferAttribute<'float'>(this.alpha, 'float');
    const wound = st.x;
    const flash = st.y;
    const charge = st.z;
    const death = st.w;
    const seed = hash(instanceIndex);
    const phase = time.mul(mix(float(0.55), float(2.4), wound.add(charge.mul(0.45)))).add(seed.mul(TAU));
    const spin = ang.add(phase);
    const collapse = oneMinus(death);
    const ringR = mix(float(0.74), float(0.1), death);
    const innerR = mix(float(0.42), float(0.04), death);

    const glow = smoothstep(1.0, 0.08, d).mul(mix(float(0.28), float(0.52), charge)).mul(collapse);

    const rim = smoothstep(0.12, 0.02, abs(d.sub(ringR)));
    const spoke = fract(spin.div(TAU).mul(16));
    const rune = smoothstep(0.12, 0.02, abs(spoke.sub(0.5)));
    const runeRing = rim.mul(mix(float(0.55), float(1.25), rune)).mul(mix(float(0.7), float(1.2), charge));

    const inner = smoothstep(0.1, 0.018, abs(d.sub(innerR))).mul(0.7).mul(collapse);

    const armSpin = fract(spin.div(TAU).mul(5).add(d.mul(2.4)));
    const arms = smoothstep(0.32, 0.03, abs(armSpin.sub(0.5)))
      .mul(smoothstep(0.88, 0.16, d))
      .mul(smoothstep(0.04, 0.18, d))
      .mul(mix(float(0.42), float(0.95), charge.add(wound.mul(0.4))));

    const n = mx_noise_float(vec3(p.mul(3.2), seed.add(time.mul(0.4)))).mul(0.12);
    const spokeCrack = (off: number) => {
      const slot = fract(ang.div(TAU).sub(hash(seed.add(off).mul(9.1))));
      const jagged = abs(slot.sub(0.5)).add(n.mul(0.35));
      return smoothstep(0.045, 0.005, jagged).mul(smoothstep(0.92, 0.14, d)).mul(smoothstep(0.05, 0.18, d));
    };
    const cracks = max(max(spokeCrack(0), spokeCrack(1.7)), max(spokeCrack(3.1), spokeCrack(4.6)))
      .mul(wound)
      .mul(mix(float(0.85), float(1.55), wound));

    const hitRing = smoothstep(0.14, 0.03, abs(d.sub(mix(float(0.62), float(0.2), death)))).mul(flash);
    const deathBloom = smoothstep(0, 0.18, death).mul(oneMinus(smoothstep(0.28, 0.72, death)));

    const flickHash = hash(vec2(time.mul(16), seed.add(instanceIndex)));
    const flicker = oneMinus(wound.mul(0.65).mul(flickHash));

    const mask = max(max(glow, runeRing), max(max(inner, arms), max(cracks, hitRing)))
      .add(deathBloom.mul(smoothstep(0.95, 0.15, d)))
      .mul(flicker)
      .mul(mix(float(1), float(0), smoothstep(0.62, 1, death)));

    material.colorNode = vec4(1, 1, 1, 1);
    material.opacityNode = mask.mul(alphaNode);

    this.mesh = new InstancedMesh(geometry, material, this.capacity);
    this.mesh.frustumCulled = false;
    this.mesh.renderOrder = 6;
    this.mesh.count = 0;
    tmpColor.setHex(0xffffff);
    for (let i = 0; i < this.capacity; i++) this.mesh.setColorAt(i, tmpColor);
  }

  begin(): void {
    this.count = 0;
  }

  /**
   * `state` is wound, flash, charge, death — each 0..1.
   * `scale` is the quad's world size (aura sits larger than the portal sprite).
   */
  push(x: number, y: number, z: number, rot: number, scale: number, tint: number, alpha: number, wound: number, flash: number, charge: number, death: number): boolean {
    if (this.count >= this.capacity) return false;
    const i = this.count++;
    tmpPos.set(x, y, z);
    tmpQuat.setFromAxisAngle(Z_AXIS, rot);
    tmpScale.set(scale, scale, 1);
    tmpMat.compose(tmpPos, tmpQuat, tmpScale);
    this.mesh.setMatrixAt(i, tmpMat);
    tmpColor.setHex(tint);
    this.mesh.setColorAt(i, tmpColor);
    this.alpha.array[i] = alpha;
    const o = i * 4;
    const s = this.state.array as Float32Array;
    s[o] = wound;
    s[o + 1] = flash;
    s[o + 2] = charge;
    s[o + 3] = death;
    return true;
  }

  end(): void {
    this.mesh.count = this.count;
    if (this.count === 0) return;
    this.mesh.instanceMatrix.needsUpdate = true;
    if (this.mesh.instanceColor) this.mesh.instanceColor.needsUpdate = true;
    this.alpha.needsUpdate = true;
    this.state.needsUpdate = true;
  }

  dispose(): void {
    this.mesh.geometry.dispose();
    (this.mesh.material as MeshBasicNodeMaterial).dispose();
  }
}
